import { ApplicationCodes } from '@makemydeal/dr-platform-types';
import type { AnyFSA } from '@makemydeal/dr-browser-utils';

import { locationStandardsReconciled } from '../actions/creators';
import { updateLocationId } from '../actions/paymentActionCreators';
import { SHOPPER_LOCATION_CHANGE_META } from '../constants';
import * as types from '../types/actionTypes';
import type { IPaymentConfig, IStateTreeWithFeatureToggles } from '../types/paymentTypes';
import {
    buildNonConditionalIncentiveKey,
    buildNonConditionalSelectionKeys,
    isProgramIncentivesRestorationEnabled
} from '../utils/incentives';
import {
    getCurrentOfferType,
    getLocationIdQuestion,
    getMappedIdsForAvailableNonConditionalIncentivesByOfferType,
    getPaymentLocationId,
    getSelectedIncentivesByOfferType,
    getSelectedNonConditionalIdsFromPenciledInputs,
    getZipCode,
    hasRecordedNonConditionalSelection
} from '../selectors';

export const STANDARDS_BEFORE_LOCATION_CHANGE = 'standardsBeforeLocationChange';

type StandardsBeforeLocationChange = { offerType: string; uncheckedIds: number[] };

const getActionZipCode = (action: AnyFSA | undefined): string | undefined =>
    (action?.payload as { zipCode?: string } | undefined)?.zipCode;

const handlesShopperLocationChange = (state: IStateTreeWithFeatureToggles, middlewareConfig: IPaymentConfig): boolean =>
    !middlewareConfig.selectors?.isManagerView &&
    middlewareConfig.sourceApplication !== ApplicationCodes.MV &&
    isProgramIncentivesRestorationEnabled(state);

const isShopperLocationChange = (
    state: IStateTreeWithFeatureToggles,
    action: AnyFSA,
    middlewareConfig: IPaymentConfig
): boolean => {
    if (action.type !== types.FETCH_QUESTIONS_OR_PAYMENT || !handlesShopperLocationChange(state, middlewareConfig)) {
        return false;
    }
    const zipCode = getActionZipCode(action);
    return zipCode !== undefined && zipCode !== getZipCode(state);
};

const getUncheckedStandards = (state: IStateTreeWithFeatureToggles): StandardsBeforeLocationChange | undefined => {
    const offerType = getCurrentOfferType(state);
    if (!hasRecordedNonConditionalSelection(state, offerType)) {
        return undefined;
    }
    const selected = new Set<number>(getSelectedNonConditionalIdsFromPenciledInputs(state, offerType));
    const uncheckedIds = getMappedIdsForAvailableNonConditionalIncentivesByOfferType(state, offerType).filter(
        (id: number) => !selected.has(id)
    );
    return { offerType, uncheckedIds };
};

export const stampShopperLocationChange = (
    state: IStateTreeWithFeatureToggles,
    action: AnyFSA,
    middlewareConfig: IPaymentConfig
): AnyFSA => {
    if (!isShopperLocationChange(state, action, middlewareConfig)) {
        return action;
    }
    const unchecked = getUncheckedStandards(state);
    return {
        ...action,
        meta: {
            ...action.meta,
            [SHOPPER_LOCATION_CHANGE_META]: true,
            ...(unchecked && { [STANDARDS_BEFORE_LOCATION_CHANGE]: unchecked })
        }
    };
};

const isCurrentLocationChangeResponse = (state: IStateTreeWithFeatureToggles, action: AnyFSA): boolean => {
    const originalAction = action.meta?.originalAction as AnyFSA | undefined;
    return (
        action.type === types.PAYMENT_RECEIVED &&
        originalAction?.meta?.[SHOPPER_LOCATION_CHANGE_META] === true &&
        !action.meta?.isStalePaymentResponse &&
        getActionZipCode(originalAction) === getZipCode(state)
    );
};

const reconcileStandardsSelection = (store: any, stamp: StandardsBeforeLocationChange): void => {
    const state: IStateTreeWithFeatureToggles = store.getState();
    const offerType = getCurrentOfferType(state);
    if (offerType !== stamp.offerType) {
        return;
    }
    const nonConditionalPrefix = buildNonConditionalIncentiveKey(offerType, '');
    const keptKeys = getSelectedIncentivesByOfferType(state, offerType).filter(
        (key: string | number) => !(typeof key === 'string' && key.startsWith(nonConditionalPrefix))
    );
    const excluded = new Set<number>(stamp.uncheckedIds);
    const availableIds = getMappedIdsForAvailableNonConditionalIncentivesByOfferType(state, offerType);
    const standardIds = availableIds.filter((id: number) => !excluded.has(id));
    const pricedWithUncheckedStandard = !getLocationIdQuestion(state) && standardIds.length < availableIds.length;
    store.dispatch(
        locationStandardsReconciled(
            [...keptKeys, ...buildNonConditionalSelectionKeys(offerType, standardIds, true)],
            pricedWithUncheckedStandard
        )
    );
};

const repriceForResolvedLocation = (store: any): void => {
    const state: IStateTreeWithFeatureToggles = store.getState();
    const locationId = getPaymentLocationId(state);
    if (locationId && getLocationIdQuestion(state)?.options?.length === 1) {
        store.dispatch(updateLocationId(locationId));
    }
};

export const reconcileAfterShopperLocationChange = (store: any, action: AnyFSA): void => {
    if (!isCurrentLocationChangeResponse(store.getState(), action)) {
        return;
    }
    const stamp = (action.meta?.originalAction as AnyFSA).meta?.[STANDARDS_BEFORE_LOCATION_CHANGE] as
        | StandardsBeforeLocationChange
        | undefined;
    if (stamp) {
        reconcileStandardsSelection(store, stamp);
    }
    repriceForResolvedLocation(store);
};
